import { getCrossBrowserFullscreenElement } from 'util/fullscreen';
import React, { useCallback, useLayoutEffect, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ControlButton } from 'ui/components/ControlButton/ControlButton';
import { ReactComponent as IconReaction } from 'static/ic-reaction.svg';
import { Popover } from '@web-chat-core/ui-kit';
import { useFullscreenStatus } from 'ui/hooks/useFullscreenStatus';
import { useRegisterAlwaysVisibleRef } from 'ui/hooks/useRegisterAlwaysVisibleRef';
import { ObstacleIds } from 'features/MutedSpeechDetection/guardController';
import { ReactionsPanel } from '../ReactionsPanel/ReactionsPanel';
import { useSendReaction } from '../../useCase/useSendReaction';
import { CenteredPopoverContent } from './view';

interface ReactionsButtonProps {
    buttonRef: React.RefObject<HTMLDivElement | null>;
    controlsContainerRef: React.RefObject<HTMLDivElement | null>;
    registerAlwaysVisibleElement?: (ref: React.RefObject<HTMLElement | null>) => () => void;
}

export const ReactionsButton = (props: ReactionsButtonProps) => {
    const { buttonRef, controlsContainerRef, registerAlwaysVisibleElement } = props;

    const { t } = useTranslation();
    const sendReaction = useSendReaction();
    const { isFullscreen } = useFullscreenStatus();

    const [isReactionsPanelOpen, setIsReactionsPanelOpen] = useState(false);
    const [transformOffset, setTransformOffset] = useState(0);
    const contentRef = useRef<HTMLDivElement>(null);

    useRegisterAlwaysVisibleRef(contentRef, registerAlwaysVisibleElement, isReactionsPanelOpen);

    const portalContainer = isFullscreen
        ? (getCrossBrowserFullscreenElement() as HTMLElement | null) || undefined
        : document.getElementById('modal-root') || undefined;

    const calculateOffset = useCallback(() => {
        const button = buttonRef?.current;
        const container = controlsContainerRef?.current;

        if (!button || !container) return 0;

        const containerRect = container.getBoundingClientRect();
        const buttonRect = button.getBoundingClientRect();

        // Центр ControlsContainer
        const containerCenter = containerRect.left + containerRect.width / 2;

        // Popover позиционируется относительно центра кнопки (align="center")
        // Текущая позиция центра контента = центр кнопки
        const currentContentCenter = buttonRect.left + buttonRect.width / 2;

        // Нужное смещение для центрирования контента относительно ControlsContainer
        return containerCenter - currentContentCenter;
    }, [buttonRef, controlsContainerRef]);

    const updateTransform = useCallback(() => {
        const offset = calculateOffset();
        setTransformOffset((prevOffset) => {
            if (offset !== prevOffset) {
                return offset;
            }
            return prevOffset;
        });
    }, [calculateOffset]);

    useEffect(() => {
        const onWindowClick = (e: PointerEvent) => {
            const isOnButtonClick = buttonRef.current?.contains(e.target as Node);
            const isOnContentClick = contentRef.current?.contains(e.target as Node);
            if (isOnButtonClick || isOnContentClick) return;
            setIsReactionsPanelOpen(false);
        };

        window.addEventListener('pointerdown', onWindowClick, true);

        return () => {
            window.removeEventListener('pointerdown', onWindowClick, true);
        };
    }, []);

    useLayoutEffect(() => {
        const button = buttonRef?.current;
        const container = controlsContainerRef?.current;
        const isRefsReady = button && container;

        if (!isReactionsPanelOpen || !isRefsReady) {
            setTransformOffset(0);
            return;
        }

        // Вычисляем начальное смещение при открытии
        setTransformOffset(calculateOffset());

        window.addEventListener('resize', updateTransform);
        window.addEventListener('scroll', updateTransform, true);

        return () => {
            window.removeEventListener('resize', updateTransform);
            window.removeEventListener('scroll', updateTransform, true);
        };
    }, [isReactionsPanelOpen, buttonRef, controlsContainerRef, calculateOffset, updateTransform]);

    const preventDefault = useCallback((e: Event) => e.preventDefault(), []);

    return (
        <Popover isOpen={isReactionsPanelOpen} onOpenChange={setIsReactionsPanelOpen} modal={false}>
            <Popover.Trigger>
                <div ref={buttonRef}>
                    <ControlButton
                        active={!isReactionsPanelOpen}
                        IconImage={IconReaction}
                        title={t('web_call_send_reactions')}
                    />
                </div>
            </Popover.Trigger>
            <Popover.Content
                side="top"
                align="center"
                onFocusOutside={preventDefault}
                onCloseAutoFocus={preventDefault}
                container={portalContainer}
            >
                <CenteredPopoverContent id={ObstacleIds.ControlReactions} ref={contentRef} $offset={transformOffset}>
                    <ReactionsPanel onReactionSelect={sendReaction} />
                </CenteredPopoverContent>
            </Popover.Content>
        </Popover>
    );
};
